import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';
import { ReceiverUser, SenderUser, UnknownObject, User } from '../../../../../../react-app-env';

import CustomStepper from '../../../../../../components/CustomStepper';
import Pathway, { PathwayStep, EnumPathwayStepComopnentType, FREETEXT_DEFAULT_CHARACTER_LIMIT } from '../../../../../../components/PathwayBuilder/Common';
import { Patient, validationErrors as validationPatientErrors, ValidationErrors as ValidationPatientErrors, PatientReducerAction } from '../../patient';

import {
  SpecialityItem,
  validationErrors as validationSpecialityErrors,
  ValidationErrors as ValidationSpecialityErrors,
  Category,
  SpecialityCategoriesReducerAction,
} from '../../speciality';

import { MultipleSpecialityPathway, validationErrors as validationSpecialityPathwayErrors, ValidationErrors as ValidationSpecialityPathwayErrors } from '../../speciality_pathway';

import { Pot, PotsReducerAction, validationErrors as validationPotsErrors, ValidationErrors as ValidationPotsErrors } from '../../pots';

import { Organisation } from '../../organisation';

import { Lab } from '../../lab';

import StepPatient from '../StepPatient';
import StepSpeciality from '../StepSpeciality';
import StepOrganisationAssignment from '../StepOrganisationAssignment';
import StepLabAssignment from '../StepLabAssignment';
import StepReview from '../StepReview';

import styles from './styles';
import { pluralise } from '../../../../../../utils/stringUtils';
import StepSamples from '../StepSamples';
import { findLast } from '../../../../../../utils/arrayUtils';
import StepPdf from '../StepPdf';
import { Document, UploadItem } from '../../../../../../utils/types';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  completeLabel: string;
  accessionNumber?: string;
  patient: Patient;
  specialityCategories: Category[];
  specialities: SpecialityItem[];
  specialityPathwayValues: MultipleSpecialityPathway;
  specialityPathway: Record<string, Pathway>;
  potPathway: Record<string, Pathway>;
  pots: Pot[];
  user: User | ReceiverUser | SenderUser | null;
  organisation?: Organisation;
  organisations: Organisation[];
  lab?: Lab;
  labs: Lab[];
  files: UploadItem[];
  setFiles: React.Dispatch<React.SetStateAction<UploadItem[]>>;
  dispatchPatient: React.Dispatch<{ type: PatientReducerAction; value: any }>;
  dispatchSpecialityCategories: React.Dispatch<{ type: SpecialityCategoriesReducerAction; value: any }>;
  dispatchSpecialityPathwayValues: React.Dispatch<{ type: string; specialityCategoryId: string; id: string; value: string }>;
  dispatchPots: React.Dispatch<{ type: PotsReducerAction; value: any }>;
  setOrganisation: React.Dispatch<React.SetStateAction<Organisation | undefined>>;
  setLab: React.Dispatch<React.SetStateAction<Lab | undefined>>;
  handleCategoryChange: (category_ids: string[]) => void;
  handleSave: () => void;
}

const CaseSteps = ({
  classes,
  completeLabel,
  accessionNumber,
  patient,
  specialities,
  specialityCategories,
  specialityPathwayValues,
  specialityPathway,
  potPathway,
  pots,
  user,
  organisation,
  organisations,
  lab,
  labs,
  files,
  setFiles,
  dispatchPatient,
  dispatchSpecialityCategories,
  dispatchSpecialityPathwayValues: dispatchSpecialityPathway,
  dispatchPots,
  setOrganisation,
  setLab,
  handleCategoryChange,
  handleSave,
}: Props): React.ReactElement => {
  const [step, setStep] = useState<number>(0);
  const [subStep, setSubStep] = useState<number>(0);
  const [selectedMode, setSelectedMode] = useState<'surgical' | string | null>('surgical');
  const [specialityCategoriesToVerifyDeletion, setSpecialityCategoriesToVerifyDeletion] = useState<string[]>([]);

  const [patientErrors, setPatientErrors] = useState<ValidationPatientErrors>({
    nhs_number: false,
    nhs_number_length: false,
    mrn: false,
    mrn_length: false,
    first_name: false,
    last_name: false,
    dob: false,
  });

  const [specialityErrors, setSpecialityErrors] = useState<ValidationSpecialityErrors>({
    id: false,
  });

  const [specialityPathwayErrors, setSpecialityPathwayErrors] = useState<Record<string, ValidationSpecialityPathwayErrors>>({});

  const [potsErrors, setPotsErrors] = useState<Record<string, ValidationPotsErrors>>({});

  const [isUnknownNhs, setIsUnknownNhs] = useState(false);

  const validate = useCallback(
    (_step: number, _subStep?: number) => {
      const errorItems: UnknownObject = {};
      if (_step === 0) {
        errorItems.nhs_number = !patient.nhs_number && !isUnknownNhs ? validationPatientErrors.nhs_number : false;
        errorItems.mrn = !patient.mrn ? validationPatientErrors.mrn : false;
        if (isUnknownNhs) {
          errorItems.first_name = !patient.first_name ? validationPatientErrors.first_name : false;
          errorItems.last_name = !patient.last_name ? validationPatientErrors.last_name : false;
          errorItems.dob = !patient.dob ? validationPatientErrors.dob : false;
        }
        setPatientErrors({ ...patientErrors, ...errorItems });
        return (Object.keys(errorItems) as Array<keyof ValidationPatientErrors>).some((i) => errorItems[i]);
      }
      if (_step === 2) {
        errorItems.id = !specialityCategories.length ? validationSpecialityErrors.id : false;
        setSpecialityErrors({ ...specialityErrors, ...errorItems });
        return (Object.keys(errorItems) as Array<keyof ValidationSpecialityErrors>).some((i) => errorItems[i]);
      }

      if (_step === 3 && _subStep !== undefined) {
        const category = specialityCategories[_subStep];
        const categoryId = category.id;

        let pathway = specialityPathwayValues[categoryId]?.pathway || {};
        const steps = specialityPathway[categoryId]?.steps || [];
        const filledStepIds = Object.keys(pathway);

        const mandatoryStepsComplete = steps
          .filter((i) => i.mandatory)
          .every((i) => {
            const value = pathway[i.id];
            if (!filledStepIds.includes(i.id)) return false;
            if (i.component_type === EnumPathwayStepComopnentType.FREETEXT) {
              return value && value.trim().length > 0;
            }
            return true;
          });

        const freetextCharacterLimitExceeded = steps
          .filter((i) => i.component_type === EnumPathwayStepComopnentType.FREETEXT)
          .some((i) => {
            const value = pathway[i.id] || '';
            const limit = i.character_limit ?? FREETEXT_DEFAULT_CHARACTER_LIMIT;
            return value.length > limit;
          });

        errorItems.incomplete = !mandatoryStepsComplete ? validationSpecialityPathwayErrors.incomplete : false;
        errorItems.characterLimitExceeded = freetextCharacterLimitExceeded ? validationSpecialityPathwayErrors.characterLimitExceeded : false;

        setSpecialityPathwayErrors((prev) => ({
          ...prev,
          [categoryId]: { ...prev[categoryId], ...errorItems },
        }));

        const surgicalIncomplete = (Object.keys(errorItems) as Array<keyof ValidationSpecialityPathwayErrors>).some((i) => errorItems[i]);

        const selectedPot = pots.find((p) => p.id === selectedMode);
        pathway = potPathway[categoryId];
        const mandatoryStepCount = pathway?.steps?.filter((s: PathwayStep) => s.mandatory).length ?? 0;

        if (selectedPot) {
          errorItems.no_name = !selectedPot.name ? validationPotsErrors.no_name : false;
          errorItems.incomplete = !selectedPot.sample_type || Object.keys(selectedPot.pathway).length < mandatoryStepCount ? validationPotsErrors.incomplete : false;
        }

        setPotsErrors((prev) => ({
          ...prev,
          [categoryId]: { ...prev[categoryId], ...errorItems },
        }));

        const potIncomplete = (Object.keys(errorItems) as Array<keyof ValidationPotsErrors>).some((i) => errorItems[i]);

        return surgicalIncomplete || potIncomplete;
      }
      return false;
    },
    [patient, pots, specialityPathwayValues, specialityPathway, patientErrors, specialityErrors, specialityPathwayErrors, potsErrors]
  );

  useEffect(() => {
    if (specialityCategories.length) {
      handleCategoryChange(specialityCategories.map((x) => x.id));
    }
  }, [specialityCategories.length, handleCategoryChange]);

  useEffect(() => {
    // In case if pot gets created switch to that pot, if gets deleted switch to last pot of the category
    setSelectedMode(pots.find((p) => p.selected)?.id || findLast(pots, (p) => p.speciality_category_id === specialityCategories[subStep]?.id)?.id || null);
  }, [pots, setSelectedMode]);

  const previousCategoryIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const currentCategoryIds = new Set(specialityCategories.map((c) => c.id));
    const previousIds = previousCategoryIds.current;

    const addedCategories = specialityCategories.filter((cat) => !previousIds.has(cat.id));
    const removedCategoryIds = Array.from(previousIds).filter((id) => !currentCategoryIds.has(id));

    addedCategories.forEach((cat) => {
      dispatchPots({
        type: PotsReducerAction.ADD_POT,
        value: {
          speciality_category_id: cat.id,
        },
      });
    });

    removedCategoryIds.forEach((id) => {
      dispatchPots({
        type: PotsReducerAction.DELETE_POT,
        value: { speciality_category_id: id },
      });
    });

    previousCategoryIds.current = currentCategoryIds;
  }, [specialityCategories, dispatchPots]);

  const isNextDisabled = () => {
    if (step === 0) {
      return (Object.keys(patientErrors) as Array<keyof ValidationPatientErrors>).some((i) => patientErrors[i]);
    }
    if (step === 2) {
      return !specialityCategories.length;
    }

    if (step === 3) {
      const currentCategory = specialityCategories[subStep];
      const categoryId = currentCategory.id;

      const errors = specialityPathwayErrors[currentCategory.id] || {};
      const surgicalIncomplete = (Object.keys(errors) as Array<keyof ValidationSpecialityPathwayErrors>).some((key) => !!errors[key]);

      const pathwaySteps = potPathway[categoryId]?.steps?.filter((step) => step.mandatory).length;
      const potsIncomplete =
        pots.some((pot) => !pot.sample_type && pot.speciality_category_id === categoryId) ||
        pots.filter((pot) => pot.speciality_category_id === categoryId).some((pot) => Object.keys(pot.pathway).length < pathwaySteps);

      return surgicalIncomplete || potsIncomplete;
    }

    if (step === 5) {
      const lbs = labs.filter((i) => !organisation || i.organisation_id === organisation.id);
      if (lbs.length === 1) {
        setLab(lbs[0]);
      }
      return !organisation;
    }
    if (step === 6) {
      return !lab;
    }
    return false;
  };

  useEffect(() => {
    // In case of any changes in steps, reset to surgical
    setSelectedMode('surgical');

    if (step === 3) {
    }
  }, [step, subStep, setSelectedMode]);

  useEffect(() => {
    if (step === 3 || !specialityCategories.length) {
      // Store ids of speciality categories to double check deletion
      setSpecialityCategoriesToVerifyDeletion(specialityCategories.map((x) => x.id));
    }
  }, [step, specialityCategories]);

  return (
    <>
      <CustomStepper
        step={step}
        subStep={subStep}
        isNextDisabled={isNextDisabled()}
        completeLabel={completeLabel}
        setStep={setStep}
        setSubStep={setSubStep}
        validate={validate}
        handleComplete={handleSave}>
        {[
          {
            label: 'Patient',
            detail: patient.first_name && patient.last_name ? `${patient.first_name} ${patient.last_name}` : null,
            gridSize: 6,
            component: (
              <StepPatient
                isUnknownNhs={isUnknownNhs}
                setIsUnknownNhs={setIsUnknownNhs}
                patient={patient}
                errors={patientErrors}
                setErrors={setPatientErrors}
                dispatch={dispatchPatient}
              />
            ),
          },
          {
            label: 'Document(s) upload',
            detail: files.length ? `${files.length} document${files.length > 1 ? 's' : ''}` : null,
            gridSize: 8,
            component: <StepPdf files={files} setFiles={setFiles} />,
          },
          {
            label: 'Specialities',
            detail: `${specialityCategories.length ? `${specialityCategories.length} speciality categories` : ''}`,
            gridSize: 8,
            component: (
              <StepSpeciality
                specialityCategories={specialityCategories}
                specialities={specialities}
                errors={specialityErrors}
                specialityCategoriesToVerifyDeletion={specialityCategoriesToVerifyDeletion}
                setErrors={setSpecialityErrors}
                dispatch={dispatchSpecialityCategories}
              />
            ),
          },
          {
            label: 'Samples',
            detail: accessionNumber,
            gridSize: 8,
            subSteps: specialityCategories.map((cat, index) => ({
              label: cat.name,
              detail: pots.filter((x) => x.speciality_category_id === cat.id).length
                ? `${pots.filter((x) => x.speciality_category_id === cat.id).length} ${pluralise('Sample', pots.filter((x) => x.speciality_category_id === cat.id).length)}`
                : '',
              component: (
                <StepSamples
                  category={cat}
                  specialityPathway={specialityPathway[cat.id]}
                  specialityPathwayValues={specialityPathwayValues[cat.id] ? specialityPathwayValues[cat.id] : {}}
                  specialityPathwayErrors={specialityPathwayErrors}
                  validate={() => validate(step, index)}
                  dispatchSpecialityPathway={dispatchSpecialityPathway}
                  pots={pots.filter((x) => x.speciality_category_id === cat.id)}
                  potPathway={potPathway[cat.id]}
                  potErrors={potsErrors}
                  potDispatch={dispatchPots}
                  selectedMode={selectedMode}
                  setSelectedMode={setSelectedMode}
                />
              ),
            })),
          },
          {
            label: 'Organisation',
            gridSize: 6,
            detail: organisation ? organisation.name : null,
            component: <StepOrganisationAssignment organisation={organisation} organisations={organisations} setOrganisation={setOrganisation} validate={() => validate(step)} />,
          },
          {
            label: 'Lab',
            gridSize: 6,
            detail: lab ? lab.name : null,
            component: (
              <StepLabAssignment lab={lab} labs={labs.filter((i) => !organisation || i.organisation_id === organisation.id)} setLab={setLab} validate={() => validate(step)} />
            ),
          },
          {
            label: 'Summary',
            gridSize: 6,
            component: (
              <StepReview
                // Pots
                pots={pots}
                potPathway={potPathway}
                // Specialities
                specialities={specialities}
                specialityCategories={specialityCategories}
                specialityPathway={specialityPathway}
                specialityPathwayValues={specialityPathwayValues}
                // Sender
                organisation={organisation}
                lab={lab}
                user={user as SenderUser}
                // Rest
                patient={patient}
                files={files}
                accessionNumber={accessionNumber}
                setStep={setStep}
              />
            ),
          },
        ]}
      </CustomStepper>
    </>
  );
};

export default withStyles(styles)(CaseSteps);
